import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';

/** A paste this browser created: the delete token is the only proof of ownership. */
export interface OwnedPaste {
  readonly id: string;
  readonly deleteToken: string;
  readonly createdAt: string;
  /** First line of the content, for listing. */
  readonly preview: string;
  readonly title?: string;
}

export interface OwnedPastesState {
  readonly byId: Readonly<Record<string, OwnedPaste>>;
}

export const emptyOwnedPastes: OwnedPastesState = { byId: {} };

const PREVIEW_LENGTH = 80;

export function previewOf(content: string): string {
  const firstLine = content.split(/\r?\n/, 1)[0] ?? '';
  return firstLine.length > PREVIEW_LENGTH ? `${firstLine.slice(0, PREVIEW_LENGTH)}…` : firstLine;
}

const ownedPastesSlice = createSlice({
  name: 'ownedPastes',
  initialState: emptyOwnedPastes,
  reducers: {
    pasteCreated(state, action: PayloadAction<OwnedPaste>) {
      state.byId[action.payload.id] = action.payload;
    },
    /** The token was used, or turned out to be invalid: we no longer own the paste. */
    pasteForgotten(state, action: PayloadAction<string>) {
      return { byId: Object.fromEntries(Object.entries(state.byId).filter(([id]) => id !== action.payload)) };
    },
  },
  selectors: {
    selectOwnedPaste: (state, id: string): OwnedPaste | undefined => state.byId[id],
    // memoised: a new array on every call would re-render subscribers needlessly
    selectOwnedPastes: createSelector(
      [(state: OwnedPastesState) => state.byId],
      (byId): readonly OwnedPaste[] => Object.values(byId).toSorted((a, b) => b.createdAt.localeCompare(a.createdAt)),
    ),
  },
});

export const { pasteCreated, pasteForgotten } = ownedPastesSlice.actions;
export const { selectOwnedPaste, selectOwnedPastes } = ownedPastesSlice.selectors;
export const ownedPastesReducer = ownedPastesSlice.reducer;
export { ownedPastesSlice };
